'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import Script from 'next/script';
import { trackPageView, trackScreenTime } from '@/lib/analytics';

const GA_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

export default function GoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const screenEnterTime = useRef<number>(Date.now());
  const prevPathname = useRef<string>('');

  useEffect(() => {
    if (!GA_ID) return;

    const url = pathname + (searchParams?.toString() ? `?${searchParams?.toString()}` : '');

    // Track screen time for previous page before navigating
    if (prevPathname?.current && prevPathname?.current !== pathname) {
      const durationSeconds = Math.round((Date.now() - screenEnterTime?.current) / 1000);
      if (durationSeconds > 0) {
        trackScreenTime(prevPathname?.current, durationSeconds);
      }
      screenEnterTime.current = Date.now();
    }

    prevPathname.current = pathname;
    trackPageView(url, document.title);
  }, [pathname, searchParams]);

  // Track screen time on unmount / tab close
  useEffect(() => {
    if (!GA_ID) return;
    screenEnterTime.current = Date.now();

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        const durationSeconds = Math.round((Date.now() - screenEnterTime?.current) / 1000);
        if (durationSeconds > 0) {
          trackScreenTime(pathname, durationSeconds);
        }
      } else {
        screenEnterTime.current = Date.now();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pathname]);

  if (!GA_ID) return null;

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`}
        strategy="afterInteractive"
      />
      <Script id="google-analytics" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA_ID}', {
            page_path: window.location.pathname,
          });
        `}
      </Script>
    </>
  );
}
